import { notifySaveSuccess } from '../utils/saveNotification';
import PageLoading from '../components/PageLoading';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge, Banner, BlockStack, Button, Icon, InlineStack, Modal, Tabs, Text, TextField } from '../components/admin-ui';
import { ArrowDownIcon, ChevronRightIcon, DeleteIcon, InfoIcon, PlusIcon, RefreshIcon, SearchIcon, XIcon } from '../components/admin-ui/icons';
import { q_products, q_p_map, c_o_u_p_map, sync_p } from "../api";
import UpsellFailures, { useUpsellFailures } from "../components/UpsellFailures";
import UpsellProduct, { UpsellProductStatus } from "../components/UpsellProduct";
import UpsellProductPicker from "../components/UpsellProductPicker";
import { matchesProduct, productIds, requireSuccess, sameProducts, upsellError } from "../utils/upsellAdmin";

export default function UpsellSetting() {
  const [products, setProducts] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draft, setDraft] = useState([]);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [loadError, setLoadError] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [syncRequested, setSyncRequested] = useState(false);
  const [picker, setPicker] = useState("");
  const [pickerError, setPickerError] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const pageRef = useRef(null);
  const alive = useRef(true);
  const failures = useUpsellFailures();
  const selected = rules.find((rule) => String(rule.id) === selectedId);
  const dirty = Boolean(selected) && !sameProducts(selected.upsell_ids, draft);
  const locked = Boolean(busy) || loading;
  const productById = useMemo(() => new Map(products.map((product) => [String(product.id), product])), [products]);
  const visibleRules = rules.filter((rule) => matchesProduct(rule.product_info || { title: `Unavailable product ${rule.product_id}` }, query));

  const selectRule = (rule) => {
    setSelectedId(rule ? String(rule.id) : "");
    setDraft(productIds(rule?.upsell_ids));
    setError("");
  };

  const loadData = useCallback(async (preferredId = "", triggerId = "") => {
    setLoading(true);
    setLoadError("");
    try {
      const responses = await Promise.allSettled([q_products({}), q_p_map({})]);
      const values = responses.map((response) => {
        if (response.status === "rejected") throw response.reason;
        const data = requireSuccess(response.value);
        if (!Array.isArray(data)) throw new Error("Could not load product rules. Please refresh.");
        return data;
      });
      if (!alive.current) return;
      setProducts(values[0]);
      setRules(values[1]);
      const next = values[1].find((rule) => triggerId ? String(rule.product_id) === triggerId : String(rule.id) === preferredId) || values[1][0];
      setSelectedId(next ? String(next.id) : "");
      setDraft(productIds(next?.upsell_ids));
    } catch (e) {
      if (alive.current) setLoadError(upsellError(e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    loadData();
    return () => { alive.current = false; };
  }, [loadData]);

  useEffect(() => {
    if (!dirty && !busy) return;
    const beforeUnload = (event) => { event.preventDefault(); event.returnValue = ""; };
    // The app uses BrowserRouter rather than a data router. Protect its
    // existing global tab navigation before the tab's onClick navigates.
    const beforeNavigation = (event) => {
      const target = event.target.closest?.('a[href], [role="tab"]');
      if (!target || pageRef.current?.contains(target)) return;
      if (busy || !window.confirm("Discard unsaved upsell changes and leave this page?")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("click", beforeNavigation, true);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
      document.removeEventListener("click", beforeNavigation, true);
    };
  }, [dirty, busy]);

  const guard = (action) => {
    if (locked) return;
    if (dirty) setPendingAction(() => action);
    else action();
  };
  const openCreate = () => guard(() => {
    setDraft(productIds(selected?.upsell_ids));
    setPickerError("");
    setPicker("create");
  });

  const saveRule = async () => {
    if (!selected || locked) return;
    setBusy("save"); setError(""); setNotice("");
    try {
      requireSuccess(await c_o_u_p_map({ id: selected.id, upsell_ids: draft }));
      setRules((current) => current.map((rule) => String(rule.id) === selectedId ? { ...rule, upsell_ids: [...draft] } : rule));
      setNotice(draft.length ? "Recommendations saved." : "Rule saved without recommendations. It will not trigger an offer."); notifySaveSuccess();
    } catch (e) { setError(upsellError(e)); }
    finally { setBusy(""); }
  };

  const submitPicker = async (choices) => {
    if (locked) return;
    if (picker === "recommendations") {
      setDraft(choices); setPicker(""); setNotice(""); return;
    }
    if (!choices[0]) return;
    setBusy("create"); setPickerError("");
    try {
      requireSuccess(await c_o_u_p_map({ product_id: choices[0], upsell_ids: [] }));
      setPicker("");
      setTab(0);
      setNotice("Rule created. Choose recommended products and save your changes.");
      await loadData("", choices[0]);
    } catch (e) { setPickerError(upsellError(e)); }
    finally { setBusy(""); }
  };

  const deleteRule = async () => {
    if (!selected || locked) return;
    setBusy("delete"); setError("");
    try {
      requireSuccess(await c_o_u_p_map({ id: selected.id, is_delete: 1 }));
      const remaining = rules.filter((rule) => String(rule.id) !== selectedId);
      setRules(remaining); selectRule(remaining[0]);
      setDeleteOpen(false); setNotice("Rule deleted. It will no longer trigger recommendations.");
    } catch (e) { setError(upsellError(e)); setDeleteOpen(false); }
    finally { setBusy(""); }
  };

  const syncProducts = async () => {
    if (locked) return;
    setBusy("sync"); setError("");
    try {
      requireSuccess(await sync_p({}));
      setSyncRequested(true);
    } catch (e) { setError(upsellError(e)); }
    finally { setBusy(""); }
  };

  return <div className="upsell-admin" ref={pageRef}>
    <header className="upsell-page-header">
      <p className="upsell-muted">Choose which products to recommend after a purchase.</p>
      <InlineStack gap="200">
        <Button icon={RefreshIcon} onClick={syncProducts} loading={busy === "sync"} disabled={locked || syncRequested}>{syncRequested ? "Sync requested" : "Sync products"}</Button>
        <Button icon={PlusIcon} variant="primary" onClick={openCreate} disabled={locked || Boolean(loadError)}>Create rule</Button>
      </InlineStack>
    </header>
    <BlockStack gap="400">
      {error && <Banner tone="critical" onDismiss={() => setError("")}>{error}</Banner>}
      {notice && <div role="status"><Banner tone="success" onDismiss={() => setNotice("")}>{notice}</Banner></div>}
      {syncRequested && <Banner tone="info" title="Product sync requested"
        action={{ content: "Refresh products", onAction: () => guard(() => loadData(selectedId)), disabled: locked }}>
        Sync runs in the background. Refresh products when it finishes to load the latest catalog.
      </Banner>}
      {tab === 0 && failures.error && <Banner tone="warning" action={{ content: "View failure records", onAction: () => setTab(1) }}>Failure records could not be refreshed. {failures.error}</Banner>}
      {tab === 0 && Number(failures.data.total) > 0 && <div className="upsell-attention">
        <Icon source={InfoIcon} tone="warning" />
        <strong>{failures.data.total} {Number(failures.data.total) === 1 ? "order needs" : "orders need"} attention</strong>
        <span>Review payment or order update issues.</span>
        <Button variant="plain" onClick={() => setTab(1)}>View failures →</Button>
      </div>}
      <div className="upsell-tabs">
        <Tabs selected={tab} onSelect={setTab} tabs={[
          { id: "upsell-rules", content: "Product rules", badge: loading ? undefined : String(rules.length), panelID: "upsell-rules-panel" },
          { id: "upsell-failures", content: "Failure records", badge: String(failures.data.total), panelID: "upsell-failures-panel" },
        ]} />
      </div>
      <section hidden={tab !== 0} id="upsell-rules-panel" role="tabpanel" aria-labelledby="upsell-rules">
        {loadError && <div className="upsell-load-error"><Banner tone="critical" action={{ content: "Reload rules", onAction: () => guard(() => loadData(selectedId)) }}>{loadError}</Banner></div>}
        {loading ? <PageLoading label="Loading product rules" message="Loading your products and rules…" />
          : !rules.length ? <div className="upsell-panel upsell-empty upsell-first-rule">
            <span className="upsell-empty-symbol"><Icon source={PlusIcon} /></span>
            <h2>Create your first product rule</h2>
            <p>Choose a purchased product, then select the extras to recommend after payment.</p>
            <Button variant="primary" onClick={openCreate} disabled={Boolean(loadError)}>Create rule</Button>
          </div>
          : <div className="upsell-workspace">
            <aside className="upsell-panel upsell-rule-list" aria-label="Trigger product rules">
              <div className="upsell-list-heading"><Text as="h2" variant="headingMd">Trigger products</Text><Badge>{String(rules.length)}</Badge></div>
              <div className="upsell-rule-search"><TextField label="Search rules" labelHidden value={query} onChange={setQuery} placeholder="Search rules" prefix={<Icon source={SearchIcon} />} autoComplete="off" clearButton onClearButtonClick={() => setQuery("")} /></div>
              <p className="upsell-list-help upsell-muted">Purchased product → recommendations</p>
              <div className="upsell-rule-scroll">
                {visibleRules.map((rule) => {
                  const count = productIds(rule.upsell_ids).length;
                  return <button type="button" key={rule.id} className={`upsell-rule-row ${String(rule.id) === selectedId ? "is-selected" : ""}`}
                    aria-pressed={String(rule.id) === selectedId} disabled={locked}
                    onClick={() => String(rule.id) !== selectedId && guard(() => { selectRule(rule); setNotice(""); })}>
                    <UpsellProduct product={rule.product_info} fallback={`Unavailable product #${rule.product_id}`}
                      detail={!rule.product_info ? <Badge tone="attention">Trigger unavailable</Badge> : count ? `${count} recommendation${count === 1 ? "" : "s"}` : <Badge tone="attention">No products selected</Badge>} />
                    <Icon source={ChevronRightIcon} tone="subdued" />
                  </button>;
                })}
                {!visibleRules.length && <div className="upsell-empty"><strong>No matching rules</strong><p>Try a different product name.</p></div>}
              </div>
              <div className="upsell-list-footer upsell-muted">{visibleRules.length} of {rules.length} product rules</div>
            </aside>
            {selected && <section className="upsell-panel upsell-editor" aria-label="Recommendation editor">
              <div className="upsell-editor-content">
                <div className="upsell-editor-heading"><Text as="h2" variant="headingMd">Edit recommendations</Text>{dirty && <Badge>Unsaved changes</Badge>}</div>
                <div className="upsell-step"><span>1</span><h3>When an order contains</h3></div>
                <div className="upsell-source-product"><UpsellProduct product={selected.product_info} fallback={`Unavailable product #${selected.product_id}`} detail="Trigger product" /><UpsellProductStatus product={selected.product_info} /></div>
                {!selected.product_info && <p className="upsell-error">This trigger product is no longer in your catalog. Delete this rule or refresh products.</p>}
                <div className="upsell-flow-arrow"><Icon source={ArrowDownIcon} tone="subdued" /></div>
                <div className="upsell-recommendation-heading">
                  <div className="upsell-step"><span>2</span><h3>Recommend these products</h3><Badge>{`${draft.length} selected`}</Badge></div>
                  <Button icon={PlusIcon} disabled={locked || Boolean(loadError)} onClick={() => { setPickerError(""); setPicker("recommendations"); }}>Add products</Button>
                </div>
                <p className="upsell-muted upsell-section-help">Customers can choose these optional additions after payment.</p>
                <div className="upsell-selected-products">
                  {draft.map((id) => {
                    const product = productById.get(id);
                    return <div className="upsell-selected-row" key={id}>
                      <UpsellProduct product={product} fallback={`Unavailable product #${id}`} />
                      <UpsellProductStatus product={product} />
                      <Button variant="tertiary" icon={XIcon} accessibilityLabel={`Remove ${product?.title || `product ${id}`}`} disabled={locked}
                        onClick={() => { setDraft((current) => current.filter((value) => value !== id)); setNotice(""); }} />
                    </div>;
                  })}
                  {!draft.length && <div className="upsell-empty"><strong>No recommended products yet</strong><p>Add products to complete this rule. A rule without recommendations does not trigger an offer.</p></div>}
                </div>
                <div className="upsell-info"><Icon source={InfoIcon} tone="subdued" /><p>Only active products with a valid price can be offered. Product eligibility is checked again at payment.</p></div>
              </div>
              <footer className="upsell-editor-footer">
                <Button tone="critical" variant="plain" icon={DeleteIcon} onClick={() => setDeleteOpen(true)} disabled={locked}>Delete rule</Button>
                <InlineStack gap="200">
                  <Button disabled={!dirty || locked} onClick={() => { setDraft(productIds(selected.upsell_ids)); setError(""); }}>Discard</Button>
                  <Button variant="primary" loading={busy === "save"} disabled={!dirty || locked || Boolean(loadError)} onClick={saveRule}>Save changes</Button>
                </InlineStack>
              </footer>
            </section>}
            <p className="upsell-how-it-works upsell-muted"><strong>How it works:</strong> purchased product → recommended products → optional add-on payment.</p>
          </div>}
      </section>
      <section hidden={tab !== 1} id="upsell-failures-panel" role="tabpanel" aria-labelledby="upsell-failures">
        <UpsellFailures controller={failures} />
      </section>
    </BlockStack>
    {picker && <UpsellProductPicker key={picker} mode={picker} products={products} selected={draft} rules={rules}
      busy={Boolean(busy)} error={pickerError} onClose={() => setPicker("")} onSubmit={submitPicker} />}
    <Modal open={Boolean(pendingAction)} onClose={() => setPendingAction(null)} title="Discard unsaved changes?"
      primaryAction={{ content: "Discard changes", destructive: true, onAction: () => { const action = pendingAction; setPendingAction(null); action?.(); } }}
      secondaryActions={[{ content: "Keep editing", onAction: () => setPendingAction(null) }]}>
      <Modal.Section><Text as="p">Your recommendation changes have not been saved. Discard them to continue, or keep editing this rule.</Text></Modal.Section>
    </Modal>
    <Modal open={deleteOpen} onClose={() => !busy && setDeleteOpen(false)} title="Delete this upsell rule?"
      primaryAction={{ content: "Delete rule", destructive: true, onAction: deleteRule, loading: busy === "delete" }}
      secondaryActions={[{ content: "Cancel", onAction: () => setDeleteOpen(false), disabled: Boolean(busy) }]}>
      <Modal.Section><Text as="p">Delete the rule for {selected?.product_info?.title || "this product"}? It will no longer trigger recommendations. Existing orders and payments will not be changed.</Text></Modal.Section>
    </Modal>
  </div>;
}
